// src/pages/OpenFoodFactsPage.js - Version corrigée avec import proper
import React from 'react';
import { Scan, AlertCircle, Camera, Sparkles } from 'lucide-react';
import { useFoodContext } from '../context';
import { useNetworkStatus } from '../hooks';
import { Button, Input, BarcodeScanner } from '../components';
import { getCategoryColor, getCategoryEmoji } from '../utils/categories';

/**
 * Product Analysis Component
 */
const ProductAnalysis = ({ product, onAdd }) => {
  if (!product.cgCalculation) return null;

  const { recommendation, warnings } = product.cgCalculation;

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
      <div className="flex gap-4">
        {product.image && (
          <img 
            src={product.image} 
            alt={product.name}
            className="w-16 h-16 object-cover rounded-lg"
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        
        <div className="flex-1">
          <h4 className="font-semibold text-gray-800 mb-1">{product.name}</h4>
          {product.brand && (
            <p className="text-sm text-gray-600 mb-2">Marque: {product.brand}</p>
          )}
          
          {/* Analyse glycémique */}
          <div className="bg-gray-50 rounded p-3 mb-3">
            <h5 className="font-medium text-gray-700 mb-2 text-sm">
              📊 Analyse glycémique (pour 100g)
            </h5>
            
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-600">IG estimé:</span>
                <span className="font-bold text-blue-600 ml-1">{product.gi}</span>
              </div>
              <div>
                <span className="text-gray-600">Glucides:</span>
                <span className="font-bold text-green-600 ml-1">{product.carbs}g</span>
              </div>
              <div>
                <span className="text-gray-600">CG (nets):</span>
                <span className="font-bold text-purple-600 ml-1">
                  {product.cgCalculation.chargeGlycemique}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Fibres:</span>
                <span className="font-bold text-orange-600 ml-1">
                  {product.cgCalculation.fibres}g
                </span>
              </div>
            </div>

            {/* Recommendation */}
            {recommendation && (
              <div className={`mt-2 p-2 rounded text-xs ${
                recommendation.level === 'très_favorable' ? 'bg-green-100 text-green-800' :
                recommendation.level === 'favorable' ? 'bg-yellow-100 text-yellow-800' :
                recommendation.level === 'modere' ? 'bg-orange-100 text-orange-800' :
                'bg-red-100 text-red-800'
              }`}>
                {recommendation.message}
              </div>
            )}

            {/* Warnings */}
            {warnings && warnings.length > 0 && (
              <div className="mt-2 space-y-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="bg-amber-50 text-amber-700 p-1 rounded text-xs">
                    ⚠️ {warning.message}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="text-xs text-gray-500 mb-2">
            Code-barres: {product.barcode}
          </div>
          
          <div className="flex items-center justify-between">
            <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(product.category)}`}>
              {getCategoryEmoji(product.category)} {product.category}
            </span>
            
            <Button
              onClick={() => onAdd(product)}
              size="sm"
            >
              Ajouter
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Open Food Facts Page Component avec Scanner Amélioré
 */
const OpenFoodFactsPage = () => {
  const {
    barcodeInput,
    setBarcodeInput,
    productNameInput,
    setProductNameInput,
    openFoodFacts,
    aiEstimate
  } = useFoodContext();

  const isOnline = useNetworkStatus();
  const [showScanner, setShowScanner] = React.useState(true); // Ouvre la caméra directement à l'arrivée sur la page
  const [lastSearchTerm, setLastSearchTerm] = React.useState('');

  const handleBarcodeSearch = () => {
    if (barcodeInput.trim()) {
      setLastSearchTerm(barcodeInput.trim());
      openFoodFacts.searchByBarcode(barcodeInput.trim());
      setBarcodeInput('');
    }
  };

  const handleNameSearch = () => {
    if (productNameInput.trim()) {
      setLastSearchTerm(productNameInput.trim());
      openFoodFacts.searchByName(productNameInput.trim());
      setProductNameInput('');
    }
  };

  const handleAIEstimate = async () => {
    if (lastSearchTerm) {
      await aiEstimate.estimateFood(lastSearchTerm);
    }
  };

  const handleAddAIFood = () => {
    const success = aiEstimate.addEstimatedFood();
    if (success) {
      openFoodFacts.clearResults();
      aiEstimate.clearEstimation();
    }
  };

  const handleAddScannedProduct = () => {
    openFoodFacts.addScannedProduct();
  };

  const handleCancelScannedProduct = () => {
    openFoodFacts.clearScannedProduct();
  };

  const handleBarcodeDetected = (code) => {
    setBarcodeInput(code);
    setShowScanner(false);
    // Lancer automatiquement la recherche (prévisualisation, pas d'ajout auto)
    setTimeout(() => {
      openFoodFacts.searchByBarcode(code);
    }, 100);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <Scan className="w-6 h-6 text-blue-600" />
          Recherche Open Food Facts avec Analyse Glycémique
        </h2>
        
        <div className="space-y-6">
          {/* Barcode Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔍 Recherche par code-barres
            </label>
            <div className="flex gap-2">
              <Input
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder="Ex: 3017624010701"
                onKeyPress={(e) => e.key === 'Enter' && handleBarcodeSearch()}
                className="flex-1 text-base text-gray-900 placeholder-gray-500"
                style={{ fontSize: '16px' }}
              />
              <Button
                onClick={handleBarcodeSearch}
                disabled={!isOnline || openFoodFacts.isSearching || !barcodeInput.trim()}
                loading={openFoodFacts.isSearching}
                className="whitespace-nowrap"
                title={!isOnline ? 'Indisponible hors ligne' : ''}
              >
                {openFoodFacts.isSearching ? 'Recherche...' : 'Rechercher'}
              </Button>
              <Button
                onClick={() => setShowScanner(true)}
                variant="success"
                className="flex items-center gap-2 whitespace-nowrap"
                disabled={!isOnline || openFoodFacts.isSearching}
                title={!isOnline ? 'Indisponible hors ligne' : ''}
              >
                <Camera className="w-4 h-4" />
                Scanner
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              📱 Scanner intelligent avec détection automatique
            </p>
          </div>

          {/* Product Name Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🏷️ Recherche par nom de produit
            </label>
            <div className="flex gap-2">
              <Input
                value={productNameInput}
                onChange={(e) => setProductNameInput(e.target.value)}
                placeholder="Ex: Nutella, Coca Cola..."
                onKeyPress={(e) => e.key === 'Enter' && handleNameSearch()}
                className="flex-1 text-base text-gray-900 placeholder-gray-500"
                style={{ fontSize: '16px' }}
              />
              <Button
                onClick={handleNameSearch}
                disabled={!isOnline || openFoodFacts.isSearching || !productNameInput.trim()}
                loading={openFoodFacts.isSearching}
                variant="success"
                className="whitespace-nowrap"
                title={!isOnline ? 'Indisponible hors ligne' : ''}
              >
                {openFoodFacts.isSearching ? 'Recherche...' : 'Rechercher'}
              </Button>
            </div>
          </div>

          {/* Prévisualisation du produit scanné par code-barres */}
          {openFoodFacts.scannedProduct && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <Scan className="w-5 h-5 text-blue-600" />
                Produit scanné - Voulez-vous l'ajouter ?
              </h3>
              <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                <ProductAnalysis
                  product={openFoodFacts.scannedProduct}
                  onAdd={handleAddScannedProduct}
                />

                <div className="flex gap-2 pt-4 border-t border-gray-200 mt-4">
                  <button
                    onClick={handleCancelScannedProduct}
                    className="flex-1 py-2 px-4 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddScannedProduct}
                    className="flex-1 py-2 px-4 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-all"
                  >
                    Ajouter à ma liste
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search Results */}
          {openFoodFacts.showSearchResults && openFoodFacts.searchResults.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                📊 Résultats avec analyse glycémique - Choisissez le bon produit :
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {openFoodFacts.searchResults.map((product, index) => (
                  <ProductAnalysis
                    key={index}
                    product={product}
                    onAdd={openFoodFacts.addSelectedProduct}
                  />
                ))}
              </div>
              
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={openFoodFacts.clearResults}
                  variant="ghost"
                >
                  Fermer les résultats
                </Button>
              </div>
            </div>
          )}

          {/* No Results Message */}
          {openFoodFacts.showSearchResults && openFoodFacts.searchResults.length === 0 && !aiEstimate.showEstimation && (
            <div className="space-y-4">
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">
                  Aucun produit trouvé
                </h3>
                <p className="text-gray-500 mb-4">
                  Essayez avec un autre terme de recherche ou un code-barres différent
                </p>

                {/* AI Estimation Option */}
                {aiEstimate.isAvailable && lastSearchTerm && (
                  <div className="max-w-md mx-auto mt-6">
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <Sparkles className="w-6 h-6 text-purple-600" />
                        <h4 className="text-lg font-semibold text-purple-800">
                          Essayer l'estimation IA ?
                        </h4>
                      </div>
                      <p className="text-sm text-purple-700 mb-4">
                        L'aliment "{lastSearchTerm}" n'est pas dans Open Food Facts.
                        Notre IA peut estimer ses valeurs nutritionnelles.
                      </p>
                      <Button
                        onClick={handleAIEstimate}
                        disabled={!isOnline || aiEstimate.isEstimating}
                        loading={aiEstimate.isEstimating}
                        variant="primary"
                        size="lg"
                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        title={!isOnline ? 'Indisponible hors ligne' : ''}
                      >
                        <Sparkles className="w-5 h-5" />
                        {aiEstimate.isEstimating ? 'Estimation en cours...' : 'Estimer avec l\'IA'}
                      </Button>
                      <p className="text-xs text-purple-600 mt-3">
                        🤖 Estimation automatique via Mistral AI
                      </p>
                    </div>
                  </div>
                )}

                {/* AI Not Available Message */}
                {!aiEstimate.isAvailable && (
                  <div className="max-w-md mx-auto mt-6">
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <p className="text-sm text-amber-700">
                        💡 <strong>Astuce :</strong> Activez l'estimation IA en configurant
                        REACT_APP_MISTRAL_API_KEY (ou REACT_APP_GROQ_API_KEY) pour obtenir des estimations automatiques.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* AI Error */}
              {aiEstimate.error && (
                <div className="max-w-md mx-auto">
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h5 className="font-medium text-red-800 mb-1">Erreur d'estimation</h5>
                        <p className="text-sm text-red-700">{aiEstimate.error}</p>
                        <Button
                          onClick={handleAIEstimate}
                          variant="outline"
                          size="sm"
                          className="mt-3 text-red-600 border-red-300"
                          disabled={!isOnline}
                          title={!isOnline ? 'Indisponible hors ligne' : ''}
                        >
                          Réessayer
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI Estimation Result */}
          {aiEstimate.showEstimation && aiEstimate.estimatedFood && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-600" />
                Estimation IA - Voulez-vous l'ajouter ?
              </h3>
              <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                <ProductAnalysis
                  product={aiEstimate.estimatedFood}
                  onAdd={handleAddAIFood}
                />

                {/* AI-specific info */}
                <div className="mt-4 space-y-3">
                  {/* Explanation */}
                  <div className="bg-blue-50 rounded-lg p-3">
                    <h5 className="font-medium text-blue-800 mb-1 text-sm">📝 Explication</h5>
                    <p className="text-xs text-blue-700">
                      {aiEstimate.estimatedFood.explanation}
                    </p>
                  </div>

                  {/* Confidence */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Confiance:</span>
                    <span className={`font-medium ${
                      aiEstimate.estimatedFood.confidence === 'élevée' ? 'text-green-600' :
                      aiEstimate.estimatedFood.confidence === 'moyenne' ? 'text-yellow-600' :
                      'text-orange-600'
                    }`}>
                      {aiEstimate.estimatedFood.confidence}
                    </span>
                  </div>

                  {/* Warning */}
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-xs text-red-700 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{aiEstimate.estimatedFood.warning}</span>
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={aiEstimate.clearEstimation}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={handleAIEstimate}
                      variant="outline"
                      size="sm"
                      disabled={!isOnline}
                      title={!isOnline ? 'Indisponible hors ligne' : ''}
                    >
                      🔄 Réessayer
                    </Button>
                  </div>

                  {/* Source */}
                  <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200">
                    Estimé par {aiEstimate.estimatedFood.estimatedBy}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {openFoodFacts.error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-700">{openFoodFacts.error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">🤖 Scanner Intelligent</h4>
          <div className="text-sm text-blue-700 space-y-2">
            <p>
              Le scanner détecte automatiquement les codes-barres et lance la recherche.
            </p>
            <div className="bg-white p-2 rounded border border-blue-300">
              <h5 className="font-medium text-blue-800 mb-1">✨ Fonctionnalités :</h5>
              <ul className="text-xs text-blue-600 space-y-1">
                <li>• Détection automatique en temps réel</li>
                <li>• Support EAN-13, Code-128, UPC-A/E</li>
                <li>• Vibration de confirmation (mobile)</li>
                <li>• Fallback saisie manuelle</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Barcode Scanner */}
      <BarcodeScanner 
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onBarcodeDetected={handleBarcodeDetected}
      />
    </div>
  );
};

export default OpenFoodFactsPage;